'use client';

import EnvelopeIcon from '@/assets/svgs/envelope.svg';
import GlobeOutlineIcon from '@/assets/svgs/globe-outline.svg';
import IdIcon from '@/assets/svgs/id.svg';
import MinusCircleSvg from '@/assets/svgs/jobs/minus-circle.svg';
import KeyboardIcon from '@/assets/svgs/keyboard.svg';
import PencilIcon from '@/assets/svgs/pencil.svg';
import TrashIcon from '@/assets/svgs/trash-can.svg';
import UserTypeIcon from '@/assets/svgs/user-type.svg';
import UsersFilledSvg from '@/assets/svgs/main/users-filled.svg';
import {
  useFetchAccountDetails,
  useUnassignUserFromAccount,
} from '@/containers/Accounts/api';
import { Modals } from '@/containers/Modals/all-modals';
import { addOpenModal } from '@/containers/Modals/redux/modals.slice';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import {
  useDeleteUser,
  useFetchUserDetails,
  useRequestPasswordReset,
  useUpdateUser,
} from '@/containers/Users/api';
import {
  canUnassignUser,
  getUnassignTooltip,
} from '@/containers/Users/utils/userAccountUtils';
import { selectAllAccounts } from '@/lib/redux/app.selectors';
import { Account } from '@/lib/redux/app.slice';
import { useAppDispatch } from '@/lib/redux/store';
import { BulkActionButton, ModalActionsContainer } from '@/shared/components/BulkActions';
import IconButton from '@/shared/components/Buttons/IconButton/IconButton';
import { ConfirmDeleteDialog } from '@/shared/components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import DetailsCard from '@/shared/components/DetailsCard';
import UserTypeBadge from '@/shared/components/UserTypeBadge';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { ModalProps, UserDetailsModalParams } from '../types';
import useModals from '../useModals';

const modalName = Modals.UserDetailsModal;

interface UserDetailsModalProps extends ModalProps {
  className?: string;
  params?: UserDetailsModalParams;
}

interface AccountWithDetails extends Account {
  isLoading?: boolean;
}

const UserDetailsModal = ({
  className,
  params,
  variant = 'narrow',
}: UserDetailsModalProps) => {
  const { isModalOpen, onClose } = useModals({
    modalName,
    shouldCloseOnEscape: true,
  });
  const { showError, showSuccess } = useToast();
  const dispatch = useAppDispatch();

  // Modal specific details
  const { userId } = params || {};

  // Fetch user details
  const {
    data: userDetails,
    isLoading,
    error,
  } = useFetchUserDetails(userId, isModalOpen);

  // Get all accounts from Redux
  const allAccountsFromRedux = useSelector(selectAllAccounts);
  const memoizedAccounts = useMemo(
    () => allAccountsFromRedux || [],
    [allAccountsFromRedux]
  );

  // State for confirmation dialogs
  const [isPasswordResetDialogOpen, setIsPasswordResetDialogOpen] = useState(false);
  const [isManualPasswordDialogOpen, setIsManualPasswordDialogOpen] = useState(false);
  const [manualPassword, setManualPassword] = useState('');
  const [manualPasswordConfirm, setManualPasswordConfirm] = useState('');
  const [manualPasswordError, setManualPasswordError] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // API mutations
  const requestPasswordResetMutation = useRequestPasswordReset();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const unassignUserMutation = useUnassignUserFromAccount();

  // Track which accounts we need to fetch
  const accountsToFetch = useMemo(() => {
    if (!userDetails?.root_accounts) return [];
    return userDetails.root_accounts.filter(
      (accountId) => !memoizedAccounts.find((acc) => acc.account_id === accountId)
    );
  }, [userDetails?.root_accounts, memoizedAccounts]);

  // Fetch missing account details
  const accountQueries = accountsToFetch.map((accountId) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useFetchAccountDetails(accountId, isModalOpen && !!userDetails)
  );

  // Combine Redux accounts with fetched accounts
  const userAccounts = useMemo(() => {
    if (!userDetails?.root_accounts) return [];

    const accounts: AccountWithDetails[] = [];

    userDetails.root_accounts.forEach((accountId) => {
      // First check Redux
      const reduxAccount = memoizedAccounts.find((acc) => acc.account_id === accountId);
      if (reduxAccount) {
        accounts.push(reduxAccount);
      } else {
        // Check if we're fetching this account
        const fetchIndex = accountsToFetch.indexOf(accountId);
        if (fetchIndex !== -1 && accountQueries[fetchIndex]) {
          const query = accountQueries[fetchIndex];
          if (query.data?.data) {
            // Account has been fetched
            const fetchedAccount = query.data.data;
            accounts.push({
              account_id: fetchedAccount.account_id,
              account_name: fetchedAccount.account_name,
              account_type: 'root',
            });
          } else if (query.isLoading) {
            // Still loading
            accounts.push({
              account_id: accountId,
              account_name: 'Loading...',
              account_type: 'root',
              isLoading: true,
            });
          } else {
            // Failed to load or not found
            accounts.push({
              account_id: accountId,
              account_name: accountId,
              account_type: 'root',
            });
          }
        } else {
          // Fallback to just showing the ID
          accounts.push({
            account_id: accountId,
            account_name: accountId,
            account_type: 'root',
          });
        }
      }
    });

    return accounts;
  }, [userDetails?.root_accounts, memoizedAccounts, accountsToFetch, accountQueries]);

  // Close modal and show error if API call fails
  useEffect(() => {
    if (error) {
      const errorMessage =
        (error as { response?: { data?: { Error?: string } } })?.response?.data?.Error ||
        'Failed to load user details';
      showError(errorMessage);
      onClose();
    }
  }, [error, showError, onClose]);

  if (!isModalOpen) return null;

  const userName = userDetails
    ? `${userDetails.first_name} ${userDetails.last_name}`
    : 'User Details';

  const handleEditUser = () => {
    if (!userDetails) return;

    dispatch(
      addOpenModal({
        modalName: Modals.EditUserDetailsModal,
        modalParams: JSON.stringify({
          userId: userDetails.user_id,
          userDetails,
        }),
      })
    );
  };

  const handleUnassignAccount = (account: AccountWithDetails) => {
    if (!userDetails) return;

    // Check if user can be unassigned from this account
    const canUnassign = canUnassignUser(
      {
        user_id: userDetails.user_id,
        root_accounts: userDetails.root_accounts,
      },
      {
        accountId: account.account_id,
        pendingUnassignments: [],
      }
    );

    if (!canUnassign) {
      showError('Cannot unassign user from their last account');
      return;
    }

    unassignUserMutation.mutate(
      {
        userId: userDetails.user_id,
        accountId: account.account_id,
      },
      {
        onSuccess: () => {
          showSuccess(`Unassigned user from ${account.account_name}`);
        },
        onError: () => {
          showError('Failed to unassign user from account');
        },
      }
    );
  };

  const handleSendResetEmail = () => {
    if (!userDetails) return;

    const domain = window.location.origin;

    requestPasswordResetMutation.mutate(
      {
        domain,
        email: userDetails.email,
      },
      {
        onSuccess: () => {
          showSuccess('Password reset email sent successfully');
          setIsPasswordResetDialogOpen(false);
        },
        onError: (error) => {
          const errorMessage =
            (error as { response?: { data?: { message?: string } } })?.response?.data
              ?.message || 'Failed to send password reset email';
          showError(errorMessage);
          setIsPasswordResetDialogOpen(false);
        },
      }
    );
  };

  const openManualPasswordDialog = () => {
    setManualPassword('');
    setManualPasswordConfirm('');
    setManualPasswordError(null);
    setIsPasswordResetDialogOpen(false);
    setIsManualPasswordDialogOpen(true);
  };

  const handleManualPasswordSubmit = () => {
    if (!userDetails) return;

    if (manualPassword.length < 6) {
      setManualPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (manualPassword !== manualPasswordConfirm) {
      setManualPasswordError('Passwords do not match.');
      return;
    }
    setManualPasswordError(null);

    updateUserMutation.mutate(
      {
        userId: userDetails.user_id,
        payload: {
          email: userDetails.email,
          first_name: userDetails.first_name,
          last_name: userDetails.last_name,
          user_type: (userDetails.user_type as 'USER' | 'ADMIN-USER') || 'USER',
          local_timezone: userDetails.local_timezone,
          root_accounts: userDetails.root_accounts || [],
          identities: userDetails.identities || [],
          user_hash: null,
          user_id: null,
          username: null,
          password: manualPassword,
        },
      },
      {
        onSuccess: () => {
          showSuccess('Password updated successfully');
          setIsManualPasswordDialogOpen(false);
          setManualPassword('');
          setManualPasswordConfirm('');
        },
        onError: (error) => {
          const errorMessage =
            (error as { response?: { data?: { message?: string } } })?.response?.data
              ?.message || 'Failed to update password';
          setManualPasswordError(errorMessage);
        },
      }
    );
  };

  const handleDeleteConfirm = () => {
    if (!userDetails) return;

    deleteUserMutation.mutate(userDetails.user_id, {
      onSuccess: () => {
        showSuccess('User deleted successfully');
        setIsDeleteDialogOpen(false);
        onClose();
      },
      onError: (error) => {
        const errorMessage =
          (error as { response?: { data?: { message?: string } } })?.response?.data
            ?.message || 'Failed to delete user';
        showError(errorMessage);
        setIsDeleteDialogOpen(false);
      },
    });
  };

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}
        shouldCloseOnBackgroundClick>
        <div className="flex h-full flex-col">
          {/* Header */}
          <ModalHeader
            modalName={modalName}
            closeAction="url"
            icon={<UsersFilledSvg className="h-5 w-5" />}
            title={isLoading ? 'Loading...' : userName}
          />

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-1 items-center justify-center p-12">
              <span className="loading loading-spinner loading-lg" />
            </div>
          )}

          {/* Content */}
          {!isLoading && userDetails && (
            <div className="flex flex-1 overflow-hidden">
              {/* Main Panel */}
              <div className="flex flex-1 flex-col overflow-y-auto p-6 pb-24">
                {/* General Information Section */}
                <div className="mb-6 grid grid-cols-[auto_1fr_3fr] items-center gap-x-4 gap-y-4">
                  {/* User ID */}
                  <IdIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-500">
                    User ID
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-300">
                    {userDetails.user_id}
                  </span>

                  {/* User Type */}
                  <UserTypeIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-500">
                    User Type
                  </span>
                  <div>
                    <UserTypeBadge type={userDetails.user_type} />
                  </div>

                  {/* Email */}
                  <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-500">
                    Email
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-300">
                    {userDetails.email}
                  </span>

                  {/* Timezone */}
                  <GlobeOutlineIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-500">
                    Timezone
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-300">
                    {userDetails.local_timezone}
                  </span>
                </div>

                {/* Accounts Section */}
                <div>
                  <DetailsCard
                    icon={<UsersFilledSvg className="h-5 w-5" />}
                    title={`Accounts (${userAccounts.length})`}>
                    {userAccounts.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="table w-full">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th className="w-24">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userAccounts.map((account) => {
                              const userCanBeUnassigned = canUnassignUser(
                                {
                                  user_id: userDetails.user_id,
                                  root_accounts: userDetails.root_accounts,
                                },
                                {
                                  accountId: account.account_id,
                                  pendingUnassignments: [],
                                }
                              );

                              return (
                                <tr key={account.account_id} className="hover">
                                  <td>
                                    <div className="flex flex-col">
                                      <span className="font-medium text-gray-900 dark:text-gray-300">
                                        {account.account_name}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {account.account_id}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <IconButton
                                      className="btn-outline border-blue-200 shadow-sm"
                                      tooltip={getUnassignTooltip(userCanBeUnassigned)}
                                      tooltipPosition="left"
                                      icon={<MinusCircleSvg />}
                                      onClick={() => handleUnassignAccount(account)}
                                      disabled={
                                        !userCanBeUnassigned ||
                                        account.isLoading ||
                                        unassignUserMutation.isPending
                                      }
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="py-8 text-center text-gray-500">
                        <div className="mb-2 text-sm">No account assignments</div>
                        <div className="text-xs text-gray-400">
                          Use the Edit button to assign accounts
                        </div>
                      </div>
                    )}
                  </DetailsCard>
                </div>
              </div>
            </div>
          )}

          {/* Modal Actions Container */}
          {!isLoading && userDetails && (
            <ModalActionsContainer>
              <BulkActionButton
                icon={<PencilIcon className="h-4 w-4" />}
                label="Edit"
                onClick={handleEditUser}
              />
              <BulkActionButton
                icon={<KeyboardIcon className="h-4 w-4" />}
                label="Reset Password"
                onClick={() => setIsPasswordResetDialogOpen(true)}
                className={requestPasswordResetMutation.isPending ? 'opacity-50' : ''}
              />
              <BulkActionButton
                icon={<TrashIcon className="h-4 w-4" />}
                label="Delete"
                onClick={() => setIsDeleteDialogOpen(true)}
                className="text-red-600 hover:bg-red-50"
              />
            </ModalActionsContainer>
          )}
        </div>
      </ModalContainer>

      {/* Password Reset choice dialog: email vs manual */}
      {isPasswordResetDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="bg-black-1000/40 absolute inset-0 z-0"
            onClick={() => setIsPasswordResetDialogOpen(false)}
          />
          <div className="relative z-10 w-full max-w-xl rounded-lg bg-white shadow-xl dark:bg-gray-900">
            <div className="flex flex-col">
              <div className="flex h-[84px] items-center justify-between px-6 pt-6">
                <div className="flex items-center justify-center gap-4">
                  <div className="bg-base-200 flex h-11 w-11 items-center justify-center rounded-lg">
                    <KeyboardIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-xl font-semibold">Reset Password</div>
                </div>
              </div>
              <div className="flex flex-col gap-3 px-6 pb-4 text-gray-700 dark:text-gray-300">
                <p>How would you like to reset this user&apos;s password?</p>
                <p className="text-sm text-gray-500">
                  Sending a reset email will email a link to{' '}
                  <strong>{userDetails?.email}</strong>. Setting manually will let you
                  enter a new password yourself.
                </p>
              </div>
              <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setIsPasswordResetDialogOpen(false)}
                  disabled={requestPasswordResetMutation.isPending}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-primary"
                  onClick={openManualPasswordDialog}
                  disabled={requestPasswordResetMutation.isPending}>
                  Set Manually
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendResetEmail}
                  disabled={requestPasswordResetMutation.isPending}>
                  {requestPasswordResetMutation.isPending ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual password reset dialog */}
      {isManualPasswordDialogOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="bg-black-1000/40 absolute inset-0 z-0"
            onClick={
              updateUserMutation.isPending
                ? undefined
                : () => setIsManualPasswordDialogOpen(false)
            }
          />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-900">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleManualPasswordSubmit();
              }}
              className="flex flex-col">
              <div className="flex h-[84px] items-center justify-between px-6 pt-6">
                <div className="flex items-center justify-center gap-4">
                  <div className="bg-base-200 flex h-11 w-11 items-center justify-center rounded-lg">
                    <KeyboardIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-xl font-semibold">Set New Password</div>
                </div>
              </div>
              <div className="flex flex-col gap-4 px-6 pb-4">
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Password</span>
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={manualPassword}
                    onChange={(e) => setManualPassword(e.target.value)}
                    className="input input-bordered w-full text-sm"
                    placeholder="At least 6 characters"
                  />
                </div>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Confirm Password</span>
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={manualPasswordConfirm}
                    onChange={(e) => setManualPasswordConfirm(e.target.value)}
                    className="input input-bordered w-full text-sm"
                  />
                </div>
                {manualPasswordError && (
                  <div className="text-error text-sm">{manualPasswordError}</div>
                )}
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setIsManualPasswordDialogOpen(false)}
                  disabled={updateUserMutation.isPending}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateUserMutation.isPending}>
                  {updateUserMutation.isPending ? 'Saving...' : 'Save Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete "${userName}"? This CANNOT be undone.`}
        confirmButtonText="Delete"
        loading={deleteUserMutation.isPending}
      />
    </div>
  );
};

export default UserDetailsModal;
